import OpenAI from "openai";
import { logError } from "./errorHandling";

export interface LessonPlanRequest {
  mataPelajaran: string;
  jenjangPendidikan: string;
  kurikulum: string;
  topik: string;
  tujuanPembelajaran: string;
  kriteriaTambahan?: string;
}

export interface LessonPlanResponse {
  success: boolean;
  content?: string;
  title?: string;
  error?: string;
  provider?: string;
  fallback?: boolean;
  responseTime?: number;
  tokensPerSecond?: number;
  metadata?: Record<string, unknown>;
}

export interface AssessmentRequest {
  mataPelajaran: string;
  jenjangPendidikan: string;
  topikTujuan: string;
  tipePertanyaan: string;
  kriteriaTambahan?: string;
  jumlahSoal?: number;
  tingkatKesulitan?: string;
  distribusiKognitif?: {
    c1c2: number;
    c3: number;
    c4c5c6: number;
  };
  includeAnswerKey?: boolean;
  includeRubric?: boolean;
  aloksiWaktu?: number;
}

export interface AssessmentResponse {
  success: boolean;
  content?: string;
  title?: string;
  answerKey?: string;
  rubric?: string;
  error?: string;
  provider?: string;
  fallback?: boolean;
  responseTime?: number;
  tokensPerSecond?: number;
  metadata?: Record<string, unknown>;
}

export interface PromptEnhancementRequest {
  currentPrompt: string;
  mataPelajaran: string;
  jenjangPendidikan: string;
  type?: "lesson" | "assessment" | "learning-objectives";
  tipePertanyaan?: string;
}

export interface PromptEnhancementResponse {
  success: boolean;
  enhancedPrompt?: string;
  error?: string;
  provider?: string;
  fallback?: boolean;
  metadata?: Record<string, unknown>;
}

export interface SumoPodAIConfig {
  model: string;
  temperature: number;
  maxTokens: number;
  apiKey: string;
  baseURL: string;
}

/**
 * SumoPod AI Service Class
 */
export class SumoPodAIService {
  private config: SumoPodAIConfig;
  private openai: OpenAI;

  constructor(config?: Partial<SumoPodAIConfig>) {
    this.config = {
      model: import.meta.env.VITE_OPENAI_MODEL || "gpt-4o-mini",
      temperature: parseFloat(import.meta.env.VITE_OPENAI_TEMPERATURE || "0.7"),
      maxTokens: parseInt(import.meta.env.VITE_OPENAI_MAX_TOKENS || "2048"),
      apiKey: import.meta.env.VITE_OPENAI_API_KEY || "",
      baseURL: "https://ai.sumopod.com/v1",
      ...config,
    };

    this.openai = new OpenAI({
      apiKey: this.config.apiKey,
      baseURL: this.config.baseURL,
      dangerouslyAllowBrowser: true,
    });
  }

  /**
   * Create a comprehensive lesson plan prompt for AI
   */
  private createLessonPlanPrompt(formData: LessonPlanRequest): string {
    const {
      mataPelajaran,
      jenjangPendidikan,
      kurikulum,
      topik,
      tujuanPembelajaran,
      kriteriaTambahan,
    } = formData;

    return `Anda adalah seorang ahli pendidikan dan perancang kurikulum yang berpengalaman dalam sistem pendidikan Indonesia. Buatlah rencana pembelajaran yang komprehensif dan profesional dengan detail berikut:

**INFORMASI PEMBELAJARAN:**
- Mata Pelajaran: ${mataPelajaran}
- Jenjang Pendidikan: ${jenjangPendidikan}
- Kurikulum: ${kurikulum}
- Topik: ${topik}
- Tujuan Pembelajaran: ${tujuanPembelajaran}
${kriteriaTambahan ? `- Kriteria Tambahan: ${kriteriaTambahan}` : ""}

**INSTRUKSI PEMBUATAN:**
Buat rencana pembelajaran yang terstruktur sesuai dengan ${kurikulum} dengan format berikut. Mulai langsung dengan judul tanpa kata pengantar atau kalimat pembuka.

## 1. IDENTITAS PEMBELAJARAN
- Mata pelajaran, kelas/jenjang, alokasi waktu (80 menit), dan kompetensi dasar yang relevan dengan ${kurikulum}

## 2. TUJUAN PEMBELAJARAN
- Minimal 3 tujuan pembelajaran yang spesifik, terukur, dapat dicapai, relevan, dan terbatas waktu (SMART)
- Menggunakan kata kerja operasional yang sesuai dengan taksonomi Bloom
- Mencakup aspek kognitif, afektif, dan psikomotorik

## 3. MATERI PEMBELAJARAN
- Konsep utama yang akan diajarkan
- Sub-konsep pendukung
- Fakta, konsep, prinsip, dan prosedur yang relevan

## 4. METODE DAN PENDEKATAN PEMBELAJARAN
- Pendekatan pembelajaran yang sesuai (saintifik, kontekstual, dll.)
- Metode pembelajaran yang bervariasi (ceramah, diskusi, praktik, dll.)
- Model pembelajaran yang tepat untuk topik ini

## 5. KEGIATAN PEMBELAJARAN

### A. Pembukaan (10 menit)
- Kegiatan pembuka yang menarik dan relevan
- Apersepsi yang menghubungkan dengan pengetahuan sebelumnya
- Penyampaian tujuan pembelajaran

### B. Kegiatan Inti (60 menit)
- Kegiatan eksplorasi, elaborasi, dan konfirmasi
- Aktivitas siswa yang beragam dan interaktif
- Penggunaan media dan sumber belajar yang tepat
- Diferensiasi pembelajaran untuk berbagai gaya belajar

### C. Penutup (10 menit)
- Kesimpulan dan refleksi pembelajaran
- Evaluasi pencapaian tujuan
- Tindak lanjut dan penugasan

## 6. MEDIA DAN SUMBER BELAJAR
- Media pembelajaran yang mendukung
- Sumber belajar yang relevan dan terpercaya
- Teknologi yang dapat diintegrasikan

## 7. PENILAIAN
- Penilaian sikap (observasi, jurnal, dll.)
- Penilaian pengetahuan (tes tertulis, lisan, penugasan)
- Penilaian keterampilan (praktik, portofolio, proyek)
- Rubrik penilaian yang jelas

## 8. REFLEKSI DAN TINDAK LANJUT
- Program remedial untuk siswa yang belum tuntas
- Program pengayaan untuk siswa yang sudah tuntas
- Refleksi guru untuk perbaikan pembelajaran selanjutnya

Pastikan rencana pembelajaran sesuai dengan ${kurikulum} dan dapat diterapkan secara praktis di kelas. Buat konten yang spesifik untuk topik "${topik}" dan tidak generic.`;
  }

  /**
   * Create assessment prompt for AI
   */
  private createAssessmentPrompt(formData: AssessmentRequest): string {
    const {
      mataPelajaran,
      jenjangPendidikan,
      topikTujuan,
      tipePertanyaan,
      kriteriaTambahan,
      jumlahSoal = 10,
      tingkatKesulitan = "sedang",
      distribusiKognitif,
      includeAnswerKey = true,
      includeRubric = true,
      aloksiWaktu = 60,
    } = formData;

    let distribusiText = "";
    if (distribusiKognitif) {
      distribusiText = `
**DISTRIBUSI KOGNITIF:**
- C1-C2 (Mengingat & Memahami): ${distribusiKognitif.c1c2}%
- C3 (Menerapkan): ${distribusiKognitif.c3}%
- C4-C6 (Menganalisis, Mengevaluasi, Menciptakan): ${distribusiKognitif.c4c5c6}%`;
    }

    return `Anda adalah seorang ahli evaluasi pendidikan dan pengembangan asesmen yang berpengalaman dalam sistem pendidikan Indonesia. Buatlah asesmen pembelajaran yang komprehensif dan berkualitas tinggi dengan detail berikut:

**INFORMASI ASESMEN:**
- Mata Pelajaran: ${mataPelajaran}
- Jenjang Pendidikan: ${jenjangPendidikan}
- Topik/Tujuan: ${topikTujuan}
- Tipe Pertanyaan: ${tipePertanyaan}
- Jumlah Soal: ${jumlahSoal}
- Tingkat Kesulitan: ${tingkatKesulitan}
- Alokasi Waktu: ${aloksiWaktu} menit${distribusiText}
${kriteriaTambahan ? `- Kriteria Tambahan: ${kriteriaTambahan}` : ""}

**INSTRUKSI PEMBUATAN:**
Buat asesmen yang dapat mengukur pencapaian pembelajaran secara akurat dengan format berikut:

## INFORMASI ASESMEN
- Mata Pelajaran, Jenjang Pendidikan, Topik, Tipe Soal, Jumlah Soal, Alokasi Waktu

## PETUNJUK UMUM
- Instruksi yang jelas dan mudah dipahami
- Panduan pengerjaan yang spesifik

## SOAL-SOAL
- Soal yang sesuai dengan tingkat kognitif yang diinginkan
- Bahasa yang jelas dan tidak ambigu
- Variasi soal yang menarik dan menantang
- Soal yang relevan dengan kehidupan siswa Indonesia
- Buat soal yang spesifik untuk topik "${topikTujuan}" dan tidak generic

${includeAnswerKey ? "## KUNCI JAWABAN\n- Jawaban yang akurat dan komprehensif\n- Penjelasan yang jelas untuk setiap jawaban" : ""}

${includeRubric ? "## RUBRIK PENILAIAN\n- Kriteria penilaian yang objektif\n- Skor yang jelas untuk setiap kriteria\n- Deskripsi tingkat pencapaian" : ""}

Pastikan asesmen dapat mengukur pencapaian pembelajaran secara akurat dan objektif. Buat konten yang spesifik dan berkualitas tinggi.`;
  }

  /**
   * Generate lesson plan using SumoPod AI
   */
  async generateLessonPlan(
    formData: LessonPlanRequest
  ): Promise<LessonPlanResponse> {
    try {
      if (!this.config.apiKey) {
        console.warn("⚠️ OpenAI API key not found, using fallback lesson plan");
        return this.createFallbackLessonPlan(formData);
      }

      const userPrompt = this.createLessonPlanPrompt(formData);

      const startTime = Date.now();

      const response = await this.openai.chat.completions.create({
        model: this.config.model,
        messages: [{ role: "user", content: userPrompt }],
        max_tokens: this.config.maxTokens,
        temperature: this.config.temperature,
      });

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      const generatedContent = response.choices[0]?.message?.content;

      if (!generatedContent) {
        throw new Error("No content generated from AI");
      }

      const title = this.generateLessonPlanTitle(formData);

      return {
        success: true,
        content: generatedContent,
        title: title,
        provider: "sumpod-openai",
        metadata: {
          responseTime,
          contentLength: generatedContent.length,
          model: this.config.model,
          usage: response.usage,
        },
      };
    } catch (error) {
      console.error("💥 Error in lesson plan generation:", error);
      logError("SumoPodAIService.generateLessonPlan", error, formData);

      // Provide fallback lesson plan
      return this.createFallbackLessonPlan(formData);
    }
  }

  /**
   * Generate assessment using SumoPod AI
   */
  async generateAssessment(
    formData: AssessmentRequest
  ): Promise<AssessmentResponse> {
    try {
      if (!this.config.apiKey) {
        console.warn("⚠️ OpenAI API key not found, using fallback assessment");
        return this.createFallbackAssessment(formData);
      }

      const userPrompt = this.createAssessmentPrompt(formData);

      const startTime = Date.now();

      const response = await this.openai.chat.completions.create({
        model: this.config.model,
        messages: [{ role: "user", content: userPrompt }],
        max_tokens: this.config.maxTokens,
        temperature: this.config.temperature,
      });

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      const generatedContent = response.choices[0]?.message?.content;

      if (!generatedContent) {
        throw new Error("No content generated from AI");
      }

      const title = this.generateAssessmentTitle(formData);

      return {
        success: true,
        content: generatedContent,
        title: title,
        provider: "sumpod-openai",
        metadata: {
          responseTime,
          contentLength: generatedContent.length,
          model: this.config.model,
          usage: response.usage,
        },
      };
    } catch (error) {
      console.error("💥 Error in assessment generation:", error);
      logError("SumoPodAIService.generateAssessment", error, formData);

      // Provide fallback assessment
      return this.createFallbackAssessment(formData);
    }
  }

  /**
   * Enhance prompt using SumoPod AI
   */
  async enhancePrompt(
    requestData: PromptEnhancementRequest
  ): Promise<PromptEnhancementResponse> {
    try {
      if (!this.config.apiKey) {
        console.warn("⚠️ OpenAI API key not found, using fallback enhancement");
        return this.createFallbackEnhancedPrompt(requestData);
      }

      const systemPrompt = this.createEnhancementSystemPrompt(requestData);
      const userPrompt = this.createEnhancementUserPrompt(requestData);

      const startTime = Date.now();

      const response = await this.openai.chat.completions.create({
        model: this.config.model,
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        max_tokens: this.config.maxTokens,
        temperature: this.config.temperature,
      });

      const endTime = Date.now();
      const responseTime = endTime - startTime;

      const enhancedPrompt = response.choices[0]?.message?.content;

      if (!enhancedPrompt) {
        throw new Error("No enhanced prompt generated from AI");
      }

      return {
        success: true,
        enhancedPrompt: enhancedPrompt,
        provider: "sumpod-openai",
        metadata: {
          responseTime,
          model: this.config.model,
          usage: response.usage,
        },
      };
    } catch (error) {
      console.error("💥 Error in prompt enhancement:", error);
      logError("SumoPodAIService.enhancePrompt", error, requestData);

      // Provide fallback enhancement
      return this.createFallbackEnhancedPrompt(requestData);
    }
  }

  /**
   * Create enhancement system prompt
   */
  private createEnhancementSystemPrompt(
    requestData: PromptEnhancementRequest
  ): string {
    const { type } = requestData;

    if (type === "assessment") {
      return `Anda adalah seorang ahli evaluasi pendidikan dan pengembangan asesmen yang berpengalaman dalam sistem pendidikan Indonesia. Tugas Anda adalah memperbaiki dan memperkaya prompt untuk pembuatan asesmen agar menjadi lebih spesifik, komprehensif, dan efektif.

Ketika menerima prompt asesmen yang sederhana atau umum, Anda akan mengembangkannya menjadi prompt yang:

1. Lebih spesifik dalam tujuan pembelajaran dan kompetensi yang ingin diukur
2. Mencakup konteks pembelajaran yang relevan dengan kehidupan siswa Indonesia
3. Menyertakan metode asesmen yang beragam dan autentik
4. Mempertimbangkan diferensiasi untuk berbagai gaya belajar siswa
5. Mengintegrasikan teknologi dan media pembelajaran yang sesuai
6. Mencakup aspek penilaian yang komprehensif
7. Memperhatikan nilai-nilai karakter dan kearifan lokal Indonesia

Berikan output dalam bentuk prompt yang natural dan conversational, bukan dalam format template atau struktur formal. Prompt yang dihasilkan harus dapat langsung digunakan untuk menghasilkan asesmen yang berkualitas tinggi.

Jangan gunakan format markdown, bullet points, atau struktur formal. Tulis dalam bentuk paragraf yang mengalir natural seperti instruksi kepada seorang guru ahli.`;
    } else if (type === "learning-objectives") {
      return `Anda adalah seorang ahli pendidikan dan perancang kurikulum yang berpengalaman dalam sistem pendidikan Indonesia. Tugas Anda adalah membuat tujuan pembelajaran (learning objectives) yang spesifik, terukur, dan sesuai dengan kurikulum Indonesia.

Ketika menerima topik pembelajaran, Anda akan membuat 5-7 tujuan pembelajaran yang:

1. Menggunakan format "Siswa mampu..." atau "Siswa dapat..."
2. Spesifik dan terukur untuk topik yang diberikan
3. Sesuai dengan jenjang pendidikan yang diminta
4. Mencakup berbagai tingkat kognitif (C1-C6)
5. Relevan dengan konteks kehidupan siswa Indonesia
6. Dapat diukur dan diamati dalam proses pembelajaran

Format output harus berupa daftar bernomor (1, 2, 3, dst) dengan setiap tujuan pembelajaran dalam satu baris. Jangan gunakan format markdown atau bullet points lainnya.

Contoh format yang diharapkan:
1. Siswa mampu menjelaskan konsep utama mengenai [topik]
2. Siswa dapat menganalisis relevansi [topik] dalam konteks kehidupan sehari-hari
3. Siswa mampu mengidentifikasi istilah penting yang berkaitan dengan [topik]
4. Siswa dapat menyusun rangkuman informatif tentang [topik]
5. Siswa mampu menyajikan hasil kajian mengenai [topik] secara kreatif`;
    } else {
      return `Anda adalah seorang ahli pendidikan dan perancang kurikulum yang berpengalaman dalam sistem pendidikan Indonesia. Tugas Anda adalah memperbaiki dan memperkaya prompt untuk pembuatan rencana pembelajaran agar menjadi lebih spesifik, komprehensif, dan efektif.

Ketika menerima prompt pembelajaran yang sederhana atau umum, Anda akan mengembangkannya menjadi prompt yang:

1. Lebih spesifik dalam tujuan pembelajaran dan kompetensi yang ingin dicapai
2. Mencakup konteks pembelajaran yang relevan dengan kehidupan siswa Indonesia
3. Menyertakan metode pembelajaran yang beragam dan interaktif
4. Mempertimbangkan diferensiasi untuk berbagai gaya belajar siswa
5. Mengintegrasikan teknologi dan media pembelajaran yang sesuai
6. Mencakup aspek penilaian yang komprehensif
7. Memperhatikan nilai-nilai karakter dan kearifan lokal Indonesia

Berikan output dalam bentuk prompt yang natural dan conversational, bukan dalam format template atau struktur formal. Prompt yang dihasilkan harus dapat langsung digunakan untuk menghasilkan rencana pembelajaran yang berkualitas tinggi.

Jangan gunakan format markdown, bullet points, atau struktur formal. Tulis dalam bentuk paragraf yang mengalir natural seperti instruksi kepada seorang guru ahli.`;
    }
  }

  /**
   * Create enhancement user prompt
   */
  private createEnhancementUserPrompt(
    requestData: PromptEnhancementRequest
  ): string {
    const {
      currentPrompt,
      mataPelajaran,
      jenjangPendidikan,
      type,
      tipePertanyaan,
    } = requestData;

    if (type === "assessment") {
      return `Tingkatkan prompt asesmen berikut untuk mata pelajaran ${mataPelajaran} jenjang ${jenjangPendidikan}${tipePertanyaan ? ` dengan tipe pertanyaan ${tipePertanyaan}` : ""}:

"${currentPrompt}"

Buatlah prompt yang lebih komprehensif dan spesifik yang akan menghasilkan asesmen berkualitas tinggi. Pastikan prompt yang dihasilkan natural dan dapat langsung digunakan.`;
    } else if (type === "learning-objectives") {
      return `Buatkan tujuan pembelajaran untuk mata pelajaran ${mataPelajaran} jenjang ${jenjangPendidikan} dengan topik "${currentPrompt}".

Buatkan 5-7 tujuan pembelajaran yang spesifik, terukur, dan sesuai dengan jenjang pendidikan tersebut. Gunakan format "Siswa mampu..." atau "Siswa dapat..." dan pastikan setiap tujuan pembelajaran mencakup berbagai tingkat kognitif.

Output harus berupa daftar bernomor tanpa format markdown lainnya.`;
    } else {
      return `Tingkatkan prompt pembelajaran berikut untuk mata pelajaran ${mataPelajaran} jenjang ${jenjangPendidikan}:

"${currentPrompt}"

Buatlah prompt yang lebih komprehensif dan spesifik yang akan menghasilkan rencana pembelajaran berkualitas tinggi. Pastikan prompt yang dihasilkan natural dan dapat langsung digunakan.`;
    }
  }

  /**
   * Generate lesson plan title
   */
  private generateLessonPlanTitle(formData: LessonPlanRequest): string {
    const { mataPelajaran, jenjangPendidikan } = formData;
    return `Rencana Pembelajaran ${mataPelajaran} - ${jenjangPendidikan?.toUpperCase() || "Jenjang Pendidikan"}`;
  }

  /**
   * Generate assessment title
   */
  private generateAssessmentTitle(formData: AssessmentRequest): string {
    const { mataPelajaran, jenjangPendidikan, tipePertanyaan } = formData;
    return `Asesmen ${mataPelajaran} (${tipePertanyaan}) - ${jenjangPendidikan?.toUpperCase() || "Jenjang Pendidikan"}`;
  }

  /**
   * Create fallback lesson plan
   */
  private createFallbackLessonPlan(
    formData: LessonPlanRequest
  ): LessonPlanResponse {
    const title = this.generateLessonPlanTitle(formData);
    const content = `# ${title}

## 1. IDENTITAS PEMBELAJARAN
- **Mata Pelajaran**: ${formData.mataPelajaran}
- **Jenjang Pendidikan**: ${formData.jenjangPendidikan}
- **Kurikulum**: ${formData.kurikulum}
- **Alokasi Waktu**: 80 menit (2 x 40 menit)
- **Topik**: ${formData.topik}
${formData.kriteriaTambahan ? `- **Kriteria Tambahan**: ${formData.kriteriaTambahan}` : ""}

## 2. TUJUAN PEMBELAJARAN
${formData.tujuanPembelajaran}

Setelah mengikuti pembelajaran ini, siswa diharapkan mampu:
1. Memahami konsep dasar ${formData.topik}
2. Menerapkan pengetahuan dalam konteks nyata
3. Mengembangkan sikap positif terhadap pembelajaran ${formData.mataPelajaran}

## 3. MATERI PEMBELAJARAN
- Konsep dasar ${formData.topik}
- Penerapan dalam kehidupan sehari-hari
- Latihan dan evaluasi pemahaman

## 4. KEGIATAN PEMBELAJARAN

### A. Pembukaan (10 menit)
- Salam, doa, dan presensi
- Apersepsi terkait materi sebelumnya
- Penyampaian tujuan pembelajaran

### B. Kegiatan Inti (60 menit)
- Penjelasan konsep ${formData.topik}
- Diskusi kelompok dan tanya jawab
- Latihan soal atau praktik langsung
- Presentasi hasil kerja siswa

### C. Penutup (10 menit)
- Kesimpulan dan refleksi pembelajaran
- Evaluasi pencapaian tujuan
- Penugasan untuk pertemuan berikutnya
- Doa penutup

## 5. PENILAIAN
- **Penilaian Sikap**: Observasi selama pembelajaran
- **Penilaian Pengetahuan**: Tes tertulis atau lisan
- **Penilaian Keterampilan**: Praktik atau presentasi

*Catatan: Ini adalah template dasar yang dapat disesuaikan dengan kebutuhan spesifik pembelajaran.*`;

    return {
      success: true,
      content: content,
      title: title,
      fallback: true,
      provider: "fallback",
      error: "OpenAI API key not configured - using fallback template",
    };
  }

  /**
   * Create fallback assessment
   */
  private createFallbackAssessment(
    formData: AssessmentRequest
  ): AssessmentResponse {
    const title = this.generateAssessmentTitle(formData);
    const content = `# ${title}

## INFORMASI ASESMEN
- **Mata Pelajaran**: ${formData.mataPelajaran}
- **Jenjang Pendidikan**: ${formData.jenjangPendidikan}
- **Topik**: ${formData.topikTujuan}
- **Tipe Soal**: ${formData.tipePertanyaan}
- **Jumlah Soal**: ${formData.jumlahSoal || 10}
- **Alokasi Waktu**: ${formData.aloksiWaktu || 60} menit

## PETUNJUK UMUM
1. Bacalah setiap soal dengan teliti sebelum menjawab
2. Kerjakan soal yang dianggap mudah terlebih dahulu
3. Periksa kembali jawaban sebelum mengumpulkan
4. Tuliskan jawaban dengan jelas dan rapi

## SOAL-SOAL

### Soal 1
Jelaskan konsep dasar dari ${formData.topikTujuan}!

### Soal 2
Berikan contoh penerapan ${formData.topikTujuan} dalam kehidupan sehari-hari!

### Soal 3
Analisislah hubungan antara ${formData.topikTujuan} dengan materi ${formData.mataPelajaran} lainnya!

[Soal-soal tambahan akan disesuaikan dengan kebutuhan spesifik]

## KUNCI JAWABAN
1. [Jawaban untuk soal 1 - konsep dasar]
2. [Jawaban untuk soal 2 - contoh penerapan]
3. [Jawaban untuk soal 3 - analisis hubungan]

*Catatan: Ini adalah template dasar yang dapat disesuaikan dengan kebutuhan spesifik asesmen.*`;

    const answerKey = `Kunci Jawaban ${title}:
1. Konsep dasar ${formData.topikTujuan}
2. Contoh penerapan dalam kehidupan sehari-hari
3. Analisis hubungan dengan materi lainnya`;

    return {
      success: true,
      content: content,
      title: title,
      answerKey: answerKey,
      fallback: true,
      provider: "fallback",
      error: "OpenAI API key not configured - using fallback template",
    };
  }

  /**
   * Create fallback enhanced prompt
   */
  private createFallbackEnhancedPrompt(
    requestData: PromptEnhancementRequest
  ): PromptEnhancementResponse {
    const { currentPrompt, mataPelajaran, jenjangPendidikan, type } =
      requestData;

    let enhancedPrompt = "";
    if (type === "assessment") {
      enhancedPrompt = `Buatlah asesmen ${mataPelajaran} yang komprehensif dan berkualitas untuk siswa ${jenjangPendidikan} dengan fokus pada "${currentPrompt}". Rancang asesmen yang dapat mengukur pencapaian pembelajaran secara akurat dengan mencakup berbagai tingkat kognitif sesuai taksonomi Bloom. Pastikan soal-soal yang dibuat jelas, tidak ambigu, dan sesuai dengan tingkat perkembangan siswa. Sertakan kunci jawaban yang lengkap dan rubrik penilaian yang objektif. Asesmen harus fair untuk semua siswa dan dapat mengidentifikasi area yang perlu diperbaiki dalam pembelajaran. Pastikan asesmen dapat mengukur pencapaian pembelajaran secara akurat dan objektif.`;
    } else if (type === "learning-objectives") {
      enhancedPrompt = `1. Siswa mampu menjelaskan konsep utama mengenai ${currentPrompt}
2. Siswa dapat menganalisis relevansi ${currentPrompt} dalam konteks kehidupan sehari-hari
3. Siswa mampu mengidentifikasi istilah penting yang berkaitan dengan ${currentPrompt}
4. Siswa dapat menyusun rangkuman informatif tentang ${currentPrompt}
5. Siswa mampu menyajikan hasil kajian mengenai ${currentPrompt} secara kreatif
6. Siswa dapat mengevaluasi informasi yang berkaitan dengan ${currentPrompt}
7. Siswa mampu menerapkan pengetahuan tentang ${currentPrompt} dalam situasi nyata`;
    } else {
      enhancedPrompt = `Buatlah rencana pembelajaran ${mataPelajaran} yang komprehensif dan menarik untuk siswa ${jenjangPendidikan} dengan fokus pada "${currentPrompt}". Rancang pembelajaran yang mencakup tujuan pembelajaran yang spesifik dan terukur, kegiatan pembelajaran yang beragam dan interaktif, serta penilaian yang autentik. Pastikan pembelajaran dapat mengakomodasi berbagai gaya belajar siswa dan mengintegrasikan nilai-nilai karakter. Sertakan juga penggunaan teknologi dan media pembelajaran yang sesuai dengan konteks siswa Indonesia. Pembelajaran harus dapat dilaksanakan secara praktis di kelas dengan mempertimbangkan keterbatasan sumber daya yang mungkin ada.`;
    }

    return {
      success: true,
      enhancedPrompt: enhancedPrompt,
      fallback: true,
      provider: "fallback",
      error: "OpenAI API key not configured - using fallback template",
    };
  }

  /**
   * Get service configuration and status
   */
  getServiceStatus(): {
    provider: string;
    model: string;
    config: SumoPodAIConfig;
    hasApiKey: boolean;
  } {
    return {
      provider: "sumpod-openai",
      model: this.config.model,
      config: this.config,
      hasApiKey: !!this.config.apiKey,
    };
  }
}

/**
 * Create a singleton instance of the SumoPod AI service
 */
let sumoPodAIServiceInstance: SumoPodAIService | null = null;

export const getSumoPodAIService = (): SumoPodAIService => {
  if (!sumoPodAIServiceInstance) {
    sumoPodAIServiceInstance = new SumoPodAIService();
  }
  return sumoPodAIServiceInstance;
};

/**
 * Legacy compatibility functions for existing code
 */
export const generateLessonPlan = async (
  formData: LessonPlanRequest
): Promise<LessonPlanResponse> => {
  const service = getSumoPodAIService();
  return await service.generateLessonPlan(formData);
};

export const generateAssessment = async (
  formData: AssessmentRequest
): Promise<AssessmentResponse> => {
  const service = getSumoPodAIService();
  return await service.generateAssessment(formData);
};

export const enhancePrompt = async (
  requestData: PromptEnhancementRequest
): Promise<PromptEnhancementResponse> => {
  const service = getSumoPodAIService();
  return await service.enhancePrompt(requestData);
};

export const enhanceAssessmentPrompt = async (
  requestData: PromptEnhancementRequest
): Promise<PromptEnhancementResponse> => {
  const service = getSumoPodAIService();
  return await service.enhancePrompt({ ...requestData, type: "assessment" });
};
